import { useState, useEffect, useRef } from 'react';
import companiesAPI from '../../api/companies';

// notifyOnType: fire onChange on every keystroke (not only on select) so free
//   text propagates to the parent — needed when the field is a controlled input.
// persistOnCreate: POST the "create new" company to the server. When false, the
//   create option just accepts the typed text locally (the caller persists later).
const CompanyAutocomplete = ({ value, onChange, placeholder, className, disabled, ariaLabel, notifyOnType = false, persistOnCreate = true }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const searchTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchCompanies = async (searchTerm) => {
    if (searchTerm.length === 0) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await companiesAPI.searchCompanies(searchTerm);
      
      // Add "create new" option at the end
      const results = [...response.data];
      results.push({
        id: 0,
        name: searchTerm,
        is_create: true
      });
      
      setSearchResults(results);
      setShowResults(true);
    } catch (error) {
      console.error('Error searching companies:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (notifyOnType) onChange(newValue);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchCompanies(newValue);
    }, 300);
  };

  const handleSelectCompany = async (company) => {
    if (company.is_create) {
      await handleCreateCompany(company);
      return;
    }
    
    setInputValue(company.name);
    setSearchResults([]);
    setShowResults(false);
    onChange(company.name);
  };

  const handleCreateCompany = async (company) => {
    // Accept the typed text without touching the server; the caller persists it.
    if (!persistOnCreate) {
      setInputValue(company.name);
      setSearchResults([]);
      setShowResults(false);
      onChange(company.name);
      return;
    }
    try {
      setIsCreating(true);
      const response = await companiesAPI.createCompany(company.name);
      
      // Select the newly created company
      setInputValue(response.data.name);
      setSearchResults([]);
      setShowResults(false);
      onChange(response.data.name);
    } catch (error) {
      console.error('Error creating company:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleInputFocus = () => {
    if (inputValue.length > 0) {
      searchCompanies(inputValue);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding results to allow for clicks
    setTimeout(() => {
      setShowResults(false);
    }, 200);
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        aria-label={ariaLabel}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
      />
      
      {showResults && searchResults.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {searchResults.map((company, index) => (
            <div
              key={company.id || index}
              onClick={() => handleSelectCompany(company)}
              className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center justify-between ${
                company.is_create ? 'bg-blue-50 text-blue-700 font-medium' : ''
              }`}
            >
              <span>
                {company.is_create ? `צור חדש: "${company.name}"` : company.name}
              </span>
              {isCreating && company.is_create && (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {isLoading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}
    </div>
  );
};

export default CompanyAutocomplete;
